import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { logger } from 'features/logger/logger';
import { defaultLocale } from '../locales';
import { DynamicBackend } from './DynamicBackend';
import {
  languageChanged,
  namespacesLoaded,
} from './store/dynamicTranslationSlice';
import type { DynamicT } from '@hub/common';
import type { Store } from '@reduxjs/toolkit';

export type { DynamicT };

// Redux store instance will be set by initializeDynamicI18n. It is not simply taken
// by import to avoid circular dependencies (details in HUB-1491).
let storeInstance: Store | undefined;

export const dynamicI18n = createInstance();
dynamicI18n
  .use(DynamicBackend)
  .use(initReactI18next)
  .init({
    lng: defaultLocale,
    debug: import.meta.env.MODE === 'development',
    cleanCode: true, // main language will be lowercased; e.g. EN ⇒ en, while leaving full locales like en-US
    load: 'currentOnly',
    ns: [],
    fallbackLng: false,
    interpolation: {
      escapeValue: false, // this would cause double-escaping, react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
  })
  .catch((error: unknown) => {
    logger.error(
      'Failed to initialize i18next for dynamic translations',
      undefined,
      error as Error
    );
  });

export const initializeDynamicI18n = (store: Store) => {
  storeInstance = store;
  DynamicBackend.storeInstance = store;
};

dynamicI18n.on('languageChanged', language => {
  logger.info(`Dynamic translation language changed to: ${language}`);

  if (!storeInstance) {
    throw new Error(
      `[ASSERT FAILED, I18N] Attempt to rely on storeInstance in dynamicI18 languageChanged event while it is not available`
    );
  }

  storeInstance.dispatch(languageChanged(language));
});

dynamicI18n.on('loaded', (recentlyLoaded: unknown) => {
  const allLoaded = getLoadedNamespacesByLanguage();
  logger.info('Dynamic translation namespaces loaded', {
    details: {
      recentlyLoaded,
      allLoaded,
    },
  });

  if (!storeInstance) {
    throw new Error(
      `[ASSERT FAILED, I18N] Attempt to rely on storeInstance in dynamicI18 loaded event while it is not available`
    );
  }

  storeInstance.dispatch(namespacesLoaded(allLoaded));
});

const getLoadedNamespacesByLanguage = (): Record<string, string[]> => {
  const store = dynamicI18n.services.resourceStore.data;
  const namespacesMap: Record<string, string[]> = {};

  Object.entries(store).forEach(([lng, namespaces]) => {
    const nonEmptyNamespaces = Object.entries(namespaces)
      .filter(([, data]) => data && Object.keys(data).length > 0)
      .map(([ns]) => ns);

    if (nonEmptyNamespaces.length > 0) {
      namespacesMap[lng] = nonEmptyNamespaces;
    }
  });

  return namespacesMap;
};
